import {
  startOfMonth,
  endOfMonth,
  subMonths,
  differenceInCalendarDays,
  isAfter,
  isValid,
  format,
} from "date-fns";
import {
  MAX_RANGE_MONTHS,
  PERIOD_TYPE,
  RATE_LIMIT_ERROR_CODE,
} from "./invoiceExport.constants";
import { utcToPickerDate } from "src/utils/timezoneFormat";

export const toApiDate = (date) => format(date, "yyyy-MM-dd");

// "Now" as wall-clock digits in the given timezone (falls back to browser-local
// when no timezone is selected) — so preset ranges like "This month"/"Last
// month" resolve against the selected zone's calendar, not the browser's.
export const zonedNow = (timeZone) =>
  timeZone ? utcToPickerDate(new Date(), timeZone) : new Date();

export const resolvePresetRange = (periodType, now = new Date()) => {
  switch (periodType) {
    case PERIOD_TYPE.THIS_MONTH:
      return { from: startOfMonth(now), to: now };
    case PERIOD_TYPE.LAST_MONTH: {
      const lastMonth = subMonths(now, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
    // "Last N months" means the N complete months before this one, excluding
    // the current partial month, so the range is stable within a given month.
    case PERIOD_TYPE.LAST_3_MONTHS: {
      const lastMonth = subMonths(now, 1);
      return { from: startOfMonth(subMonths(now, 3)), to: endOfMonth(lastMonth) };
    }
    case PERIOD_TYPE.LAST_6_MONTHS: {
      const lastMonth = subMonths(now, 1);
      return { from: startOfMonth(subMonths(now, 6)), to: endOfMonth(lastMonth) };
    }
    default:
      return null;
  }
};

export const validateRange = ({ from, to }, now = new Date()) => {
  if (!from || !to || !isValid(from) || !isValid(to)) {
    return "Select both a start and an end date.";
  }
  if (isAfter(from, to)) {
    return "Start date must be before the end date.";
  }
  if (isAfter(to, now)) {
    return "End date cannot be in the future.";
  }
  // Compare against the same calendar day N months back rather than counting
  // days, so month lengths don't make the boundary drift.
  const earliestAllowed = subMonths(to, MAX_RANGE_MONTHS);
  if (differenceInCalendarDays(from, earliestAllowed) < 0) {
    return `You can request a maximum of ${MAX_RANGE_MONTHS} months at a time.`;
  }
  return null;
};

/**
 * Build the request payload for CMS API from the current form selection.
 */
export const buildExportPayload = ({ periodType, customFrom, customTo, timeZone }) => {
  const range =
    periodType === PERIOD_TYPE.CUSTOM
      ? { from: customFrom, to: customTo }
      : resolvePresetRange(periodType, zonedNow(timeZone));

  if (!range) return null;

  return {
    periodFrom: toApiDate(range.from),
    periodTo: toApiDate(range.to),
    periodType,
  };
};

export const isRateLimitError = (error) => {
  const data = error?.response?.data;
  return (
    data?.code === RATE_LIMIT_ERROR_CODE ||
    (error?.response?.status === 429 && !data?.code)
  );
};

export const getExportErrorMessage = (error) => {
  if (isRateLimitError(error)) {
    return "This team has reached the limit of 2 invoice export requests per day. Please try again tomorrow.";
  }
  return (
    error?.response?.data?.message ??
    error?.response?.data?.status_message ??
    "Could not submit your export request. Please try again."
  );
};

export const parseSasExpiry = (url) => {
  if (!url || typeof url !== "string") return null;
  try {
    const expiry = new URL(url).searchParams.get("se");
    if (!expiry) return null;
    const parsed = new Date(expiry);
    return isValid(parsed) ? parsed : null;
  } catch {
    // Malformed URL — nothing useful to show, so show nothing.
    return null;
  }
};

export const getAccessExpiry = (row) => {
  if (row?.expiresAt) {
    const parsed = new Date(row.expiresAt);
    if (isValid(parsed)) return parsed;
  }
  return parseSasExpiry(row?.downloadUrl);
};

export const formatAccessWindow = (row, now = new Date()) => {
  const expiry = getAccessExpiry(row);
  if (!expiry) return null;

  const daysLeft = differenceInCalendarDays(expiry, now);
  if (daysLeft < 0) return "Expired";
  if (daysLeft === 0) return "Available until today";
  if (daysLeft === 1) return "Available until tomorrow";
  return `Available until ${format(expiry, "dd MMM yyyy")}`;
};

export const isAccessExpired = (row, now = new Date()) => {
  const expiry = getAccessExpiry(row);
  return expiry ? expiry.getTime() <= now.getTime() : false;
};

export const parseExportParts = (parts) => {
  if (Array.isArray(parts)) return parts;
  if (typeof parts === "string" && parts.trim()) {
    try {
      const parsed = JSON.parse(parts);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const formatPartLabel = (part, index) => {
  if (part?.label) return part.label;
  if (part?.periodFrom && part?.periodTo) {
    return formatPeriod(part.periodFrom, part.periodTo);
  }
  return `Part ${part?.part ?? index + 1}`;
};

// Formats a stored row's period for display in the Download Center.
export const formatPeriod = (periodFrom, periodTo) => {
  if (!periodFrom || !periodTo) return "-";
  const from = new Date(periodFrom);
  const to = new Date(periodTo);
  if (!isValid(from) || !isValid(to)) return "-";
  return `${format(from, "dd MMM yyyy")} - ${format(to, "dd MMM yyyy")}`;
};
